/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import Button from 'carbon-react/lib/components/button';

import { FormattedMessage } from '@futrli/localisation';

import { ReportIntlProvider } from '../report-intl-provider/report-intl-provider';
import { ReportRenderer } from '../report-renderer/report-renderer';
import { ReportTitle } from '../report-title/report-title';
import { useNavHeight } from '../../../hooks/useNavHeight';
import { useReportBuilderContext } from '../../report-builder.context';
import { useReportsContext } from '../../reports.context';

import styles from './report-view.scss';

interface Props {
  onEdit?: () => void;
}

export const ReportView = ({ onEdit }: Props) => {
  const {
    reportId,
    isReady,
    pageSetup: { orientation, backgroundColor },
    setWorkAreaWidth,
    locale
  } = useReportBuilderContext();
  const { navHeight } = useNavHeight();
  const { reportViewRef, setReportPendingPrint, reportPendingPrint, handlePrint, isPrinting } = useReportsContext();
  const [readyToPrint, setReadyToPrint] = useState(false);
  const workAreaRef = useRef<HTMLDivElement | null>(null);
  const workAreaHeight = `calc(100vh - ${navHeight}px)`;

  useEffect(() => {
    if (reportPendingPrint && reportPendingPrint === reportId && !isReady) {
      setReadyToPrint(true);
    }
  }, [reportPendingPrint, reportId, isReady]);

  useEffect(() => {
    if (reportPendingPrint && reportPendingPrint === reportId && isReady && readyToPrint) {
      // waiting till all rendering and animations are done
      setTimeout(() => {
        handlePrint();
      }, 500);
      setReportPendingPrint(undefined);
      setReadyToPrint(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPendingPrint, reportId, isReady]);

  useLayoutEffect(() => {
    const updateWorkAreaWidth = () => {
      setWorkAreaWidth((workAreaRef.current?.clientWidth || 120) - 120);
    };
    updateWorkAreaWidth();

    window.addEventListener('resize', updateWorkAreaWidth);

    return () => {
      window.removeEventListener('resize', updateWorkAreaWidth);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workAreaRef.current?.clientWidth]);

  return (
    <div
      className={clsx(styles.viewContainer, styles.withNoSidebar)}
      ref={workAreaRef}
      style={{ height: workAreaHeight, ...(orientation === 'dashboard' ? { backgroundColor } : {}) }}
      id="viewContainer"
    >
      <ReportIntlProvider locale={locale}>
        <div className={styles.reportHeader}>
          <ReportTitle />
          {onEdit && !isPrinting && (
            <Button buttonType="secondary" onClick={onEdit}>
              <FormattedMessage id="common.edit" defaultMessage="Edit" />
            </Button>
          )}
        </div>
        <ReportRenderer
          isDashboard={orientation === 'dashboard'}
          printState={isPrinting}
          containerRef={reportViewRef}
        />
      </ReportIntlProvider>
    </div>
  );
};
